import React, { useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import PercentIcon from "@mui/icons-material/Percent";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { IconButton, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Slider from "@mui/material/Slider";
import InterestPdf from "../pdf/pdf-interest";
import ReactGA from 'react-ga4'
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { InterestCalculation } from "../models/interest";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { setCurrencySymbol } from "../Redux/currencySlice";
import { saveInterestCalculation, updateInterestCalculation } from "../Database/interest";
import { useLocation } from "react-router-dom";
import BootstrapTooltip from "../components/tooltip";
import SavedSnackbar from "../utils/saved-snackbar";

ChartJS.register(ArcElement, Tooltip, Legend);

interface ErrorMessages {
  principal?: string;
  rate?: string;
  period?: string;
  startDate?: string;
  endDate?: string;
}

const InterestCalculator: React.FC<{ interestCalculator?: InterestCalculation }> = ({ }) => {
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const [interestData, setInterestData] = useState<InterestCalculation | null>(null);

  const [interestType, setInterestType] = useState<"simple" | "compound">(
    "simple"
  );
  const [principal, setPrincipal] = useState("10000");
  const [rate, setRate] = useState("5");
  const [compoundInterval, setCompoundInterval] = useState<
    "year" | "half-year" | "month"
  >("year");
  const [period, setPeriod] = useState<number | null>(5);
  const [periodUnit, setPeriodUnit] = useState<"years" | "months">("years");
  // const [interestRate, setInterestRate] = useState<string>("");

  const today = new Date().toISOString().split("T")[0];
  const oneYearLater = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  )
    .toISOString()
    .split("T")[0];

  const [startDate, setStartDate] = useState(today);
  // const [calculation, setCalculation] = useState<{ id?: number; title?: string; principal?: number; rate?: number; period?: number; currencySymbol?: string } | null>(null);
  const [endDate, setEndDate] = useState(oneYearLater);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [showResult, setShowResult] = useState(false);
  const [amount, setAmount] = useState(10000);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [showInterestDatePdf, setShowInterestDatePdf] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const currencySymbolFromStore = useSelector((state: RootState) => state.country.currencySymbol);
  const dispatch = useDispatch();
  const location = useLocation()
  const [principalForChart, setPrincipalForChart] = useState(0);
  const [interestForChart, setInterestForChart] = useState(0);
  const [savedTitle, setSavedTitle] = useState("");
  const [errors, setErrors] = useState({
    principal: "",
    rate: "",
    period: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const MAX_PRINCIPAL = 10000000;

  const handleReset = () => {
    ReactGA.event({ 'category': 'Interest Calculator', 'action': 'Interest Reset Button', 'label': 'Interest Calculator' })
    setPrincipal("10000");
    setRate("5");
    setCompoundInterval("year");
    setStartDate(today);
    setEndDate(oneYearLater);
    setPeriod(1);
    setTotalInterest(null);
    setTotalAmount(null);
    setErrorMessages({});
    setShowResult(false);
    setShowPdfButton(false);
    setShowInterestDatePdf(false);
    setTitle("");
    setSavedTitle("");

  };

  const formatNumberInIndianStyle = (num: number | string) => {
    if (!num) return "";
    const [integerPart, decimalPart] = num.toString().split(".");

    const lastThreeDigits = integerPart.slice(-3);
    const otherDigits = integerPart.slice(0, -3);
    const formattedInteger =
      otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
      (otherDigits.length > 0 ? "," : "") +
      lastThreeDigits;

    return decimalPart
      ? `${formattedInteger}.${decimalPart}`
      : formattedInteger;
  };

  // const calculateMaturityAmount = (principal: number, rate: number, period: number): number => {
  //   return principal + (principal * (rate / 100) * period);
  // };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }

    const simpleInterestCalculation: InterestCalculation = {
      principal: parseFloat(principal.replace(/,/g, "")),
      rate: parseFloat(rate),
      period: parseInt(period?.toString() || '0', 10),
      periodUnit: periodUnit,
      currencySymbol,
      title,
      compoundInterval: compoundInterval,
      startDate,
      endDate,
      interestType

    };

    try {
      if (interestData?.id) {
        await updateInterestCalculation(interestData.id, simpleInterestCalculation);
        console.log("Interest Calculation updated successfully.");
      } else {
        await saveInterestCalculation(simpleInterestCalculation);
        console.log("Simple Interest Calculation saved successfully.");
      }
    } catch (error) {
      console.error("Error saving/updating Simple Interest Calculation:", error);
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");
  };

  useEffect(() => {
    const { simpleInterest: passedSimpleInterest, currencySymbol: passedCurrencySymbol } = location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      Symbol(passedCurrencySymbol);
    } else {
      Symbol(currencySymbol);
    }

    if (passedSimpleInterest) {
      setInterestData(passedSimpleInterest)
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setPrincipal(passedSimpleInterest.principal.toString());
      setRate(passedSimpleInterest.rate.toString());
      setPeriod(passedSimpleInterest.period.toString());
      setTitle(passedSimpleInterest.title);
      setStartDate(passedSimpleInterest.startDate.toString())
      setEndDate(passedSimpleInterest.endDate.toString())
      setCompoundInterval(passedSimpleInterest.compoundInterval.toString())
      setInterestType(passedSimpleInterest.interestType.toString())
      setPeriodUnit(passedSimpleInterest.periodUnit);
      setSavedTitle(passedSimpleInterest.title || "");


    }
    handleCalculate();
  }, [location.state, interestData]);


  const validateInputs = () => {
    const newErrors = {
      principal: "",
      rate: "",
      period: "",
    };
    let isValid = true;

    const minPrincipal = 1000;
    const minRate = 1;
    const maxRate = 30;
    const MAX_PERIOD = 30;


    if (!principal) {
      newErrors.principal = "Principal amount is mandatory.";
      isValid = false;
    } else if (parseFloat(principal.replace(/,/g, "")) < minPrincipal) {
      newErrors.principal = `Principal amount must be at least ` + currencySymbol + `${minPrincipal}.`;
      isValid = false;
    }

    if (!rate) {
      newErrors.rate = "Interest rate is mandatory.";
      isValid = false;
    } else if (parseFloat(rate) < minRate) {
      newErrors.rate = `Interest rate must be at least ${minRate}%.`;
      isValid = false;
    } else if (parseFloat(rate) > maxRate) {
      newErrors.rate = `Interest rate cannot exceed ${maxRate}%.`;
      isValid = false;
    }

    // if (!period) {
    //   newErrors.period = "Period is mandatory.";
    //   isValid = false;
    // } else if (parseFloat(period) < minPeriod) {
    //   newErrors.period = `Period must be at least ${minPeriod} year.`;
    //   isValid = false;
    // } else if (parseFloat(period) > maxPeriod) {
    //   newErrors.period = `Period cannot exceed ${maxPeriod} years.`;
    //   isValid = false;
    // }

    if (periodUnit === "months" && (!period || period < 1 || period > 120)) {
      newErrors.period = "Period must be between 1 and 120 months.";
      isValid = false;
    } else if (
      periodUnit === "years" &&
      (!period || period < 1 || period > MAX_PERIOD)
    ) {
      newErrors.period = "Period must be between 1 and 10 years.";
      isValid = false;
    }

    if (!period) {
      newErrors.period = "Period unit (years or months) is mandatory.";
      isValid = false;
    }

    setErrorMessages(newErrors);
    return isValid;
  };
  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLimit: number,
    decimalLimit: number = 2
  ) => {
    const value = e.target.value;
    const validValue = value.replace(/[^0-9.]/g, "");

    if (value.length > 0 && value[0] === "0" && value !== "0") {
      return;
    }
    if (value === "0") {
      return "";
    }

    const parts = validValue.split(".");
    if (parts.length > 1 && parts[1].length > decimalLimit) {
      parts[1] = parts[1].slice(0, decimalLimit);
    }

    const formattedValue = parts.join(".");

    if (parseFloat(formattedValue) <= maxLimit || formattedValue === "") {
      setter(formattedValue);
    }
  };

  const handleCalculate = () => {
    ReactGA.event({ 'category': 'Interest calculator', 'action': 'Interest Calculate Button', 'label': 'Interest Calculator' })
    const principalValue = parseFloat(principal.replace(/,/g, ""));
    const rateValue = parseFloat(rate);
    const periodValue = period || 0;
    let interest = 0;

    let hasError = false;
    setErrorMessages({});
    setShowResult(true);
    if (!validateInputs()) {
      return;
    }

    if (!principal || principalValue <= 0) {
      setErrorMessages((prev) => ({
        ...prev,
        principal: "Please fill in a valid Principal Amount.",
      }));
      hasError = true;
    }

    if (!rate || rateValue <= 0) {
      setErrorMessages((prev) => ({
        ...prev,
        rate: "Please fill in a valid Interest Rate.",
      }));
      hasError = true;
    }

    if (
      interestType === "simple" &&
      (!period || isNaN(periodValue) || periodValue <= 0)
    ) {
      setErrorMessages((prev) => ({
        ...prev,
        period: "Please fill in a valid Period.",
      }));
      hasError = true;
    }

    if (interestType === "compound") {
      if (!startDate) {
        setErrorMessages((prev) => ({
          ...prev,
          startDate: "Please select a Start Date.",
        }));
        hasError = true;
      }
      if (!endDate) {
        setErrorMessages((prev) => ({
          ...prev,
          endDate: "Please select an End Date.",
        }));
        hasError = true;
      } else if (new Date(startDate) > new Date(endDate)) {
        setErrorMessages((prev) => ({
          ...prev,
          endDate: "End Date must be after Start Date.",
        }));
        hasError = true;
      }
    }

    if (hasError) return;

    const maxPrincipal = 10000000;
    const maxRate = 30;

    if (principalValue > maxPrincipal) {
      setErrorMessages((prev) => ({
        ...prev,
        principal: `Principal amount cannot exceed ₹${maxPrincipal.toLocaleString()}.`,
      }));
      return;
    }
    if (rateValue > maxRate) {
      setErrorMessages((prev) => ({
        ...prev,
        rate: `Rate cannot exceed ${maxRate}%.`,
      }));
      return;
    }

    if (interestType === "simple") {
      const time = periodUnit === "years" ? periodValue : periodValue / 12;
      interest = (principalValue * rateValue * time) / 100;
    } else if (interestType === "compound") {
      let compoundFactor = 1;
      const time = periodUnit === "years" ? periodValue : periodValue / 12;

      if (compoundInterval === "year") {
        compoundFactor = 1;
      } else if (compoundInterval === "half-year") {
        compoundFactor = 2;
      } else if (compoundInterval === "month") {
        compoundFactor = 12;
      }

      const ratePerPeriod = rateValue / (100 * compoundFactor);
      const periods = compoundFactor * time;
      interest =
        principalValue * Math.pow(1 + ratePerPeriod, periods) - principalValue;
    }

    setTotalInterest(interest);
    setPrincipal(principal);
    setTotalAmount(principalValue + interest);
    setPrincipalForChart(principalValue);
    setInterestForChart(interest);
    setShowPdfButton(true);


  };
  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const total = principalForChart + interestForChart
  const percentagePrincipal = Math.round((principalForChart / total) * 100)
  const percentageInterest = Math.round((interestForChart / total) * 100)
  const pieChartData = {
    labels: ["Principal%", "Interest%"],
    datasets: [
      {
        data: [
          percentagePrincipal,
          percentageInterest,
        ],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };

  const formatNumberWithCommas = (value: number | string) => {
    if (typeof value === 'number') {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return value;
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>Interest Calculator</title>
        <meta
          name="description"
          content="Easily calculate your loan interest with our Interest Calculator. Get detailed total interest, and balances to make informed financial decisions."
        />
      </Helmet>

      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      <div className="container mt-5" onKeyPress={handleKeyPress}>
        <div className="row justify-content-center">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
              Interest Calculator
            </h5>
          </div>
          <p>An Interest Calculator helps you determine the interest earned on your investments or loans based on the principal amount, interest rate, and duration. This tool is essential for budgeting, comparing different investment options, and planning your financial future effectively.</p>
          {savedTitle && (
            <Typography variant="h6" style={{ marginLeft: '20px' }}>
              {savedTitle}
            </Typography>
          )}
          <div className="col-12 col-md-6 mb-4">
            <div className="card shadow-sm" style={{ width: '100%', height: '570px', margin: '0 auto', overflow: 'auto' }}>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Select Interest Type:</label>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <div
                      className="btn-group btn-group-toggle w-100"
                      role="group"
                      data-toggle="buttons"
                    >
                      <input
                        type="radio"
                        className="btn-check"
                        name="interestType"
                        id="simpleInterest"
                        value="simple"
                        checked={interestType === "simple"}
                        onChange={() => setInterestType("simple")}
                      />
                      <label
                        className="btn btn-outline-primary w-50"
                        htmlFor="simpleInterest"
                      >
                        Period
                      </label>
                      <input
                        type="radio"
                        className="btn-check"
                        name="interestType"
                        id="compoundInterest"
                        value="compound"
                        checked={interestType === "compound"}
                        onChange={() => setInterestType("compound")}
                      />
                      <label
                        className="btn btn-outline-primary w-50"
                        htmlFor="compoundInterest"
                      >
                        Date
                      </label>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="principal" className="form-label">
                    Principal Amount ({currencySymbol})
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="principal"
                    value={formatNumberInIndianStyle(principal)}
                    onChange={(e) => {
                      const inputValue = e.target.value.replace(/,/g, "");

                      const regex = /^(10000000|[1-9][0-9]{0,7})$/;

                      if (regex.test(inputValue) || inputValue === "") {
                        setPrincipal(inputValue);
                      }

                    }}
                    onBlur={() => {
                      const numericValue = parseInt(
                        principal.replace(/,/g, "")
                      );
                      if (!isNaN(numericValue) && numericValue <= 10000000) {
                        setPrincipal(
                          numericValue.toString()
                        );
                      } else {
                        setPrincipal("");
                        setErrorMessages((prev) => ({
                          ...prev,
                          principal:
                            `Principal amount must be between 1,000 and 1,00,00,000.`,
                        }));
                      }
                    }}
                    placeholder="Enter Principal Amount"
                  />
                  {errorMessages.principal && (
                    <div className="text-danger">{errorMessages.principal}</div>
                  )}
                  <div className="mb-3 d-flex justify-content-center">
                    <Slider
                      min={1000}
                      max={MAX_PRINCIPAL}
                      step={100000}
                      value={parseFloat(principal.replace(/,/g, ""))}
                      onChange={(e, newValue) =>
                        setPrincipal(newValue.toString())
                      }
                      valueLabelDisplay="auto"
                      shiftStep={30}
                      sx={{ width: "95%" }}
                    />
                  </div>
                </div>


                <div className="mb-3">
                  <label htmlFor="rate" className="form-label">
                    Interest Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="rate"
                      value={rate.toString()}
                      onChange={(e) => handleNumberInput(e, setRate, 30, 2)}
                      placeholder="Enter Interest Rate"
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>
                  </div>
                  {errorMessages.rate && (
                    <div className="text-danger">{errorMessages.rate}</div>
                  )}
                </div>

                {interestType === "simple" ? (
                  <div className="mb-3">
                    <label htmlFor="period" className="form-label">
                      Period
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${errors.period ? "is-invalid" : ""
                          }`}
                        id="period"
                        value={period !== null ? period : ""}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          if (value === "") {
                            setPeriod(null);
                            setErrors((prev) => ({ ...prev, period: "" }));
                          } else {
                            const numericValue = parseFloat(value);
                            if (
                              !isNaN(numericValue) &&
                              ((periodUnit === "years" &&
                                numericValue >= 1 &&
                                numericValue <= 10) ||
                                (periodUnit === "months" &&
                                  numericValue >= 1 &&
                                  numericValue <= 120))
                            ) {
                              setPeriod(numericValue);
                              setErrors((prev) => ({ ...prev, period: "" }));
                            } else {
                              if (periodUnit === "years") {
                                if (numericValue > 10) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    period: "Maximum period is 10 years.",
                                  }));
                                }
                              } else if (periodUnit === "months") {
                                if (numericValue > 120) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    period: "Maximum period is 120 months.",
                                  }));
                                }
                              }
                            }
                          }
                        }}
                      />
                      <button
                        className={`btn ${periodUnit === "years"
                          ? "btn-primary"
                          : "btn-outline-primary"
                          }`}
                        type="button"
                        onClick={() => setPeriodUnit("years")}
                      >
                        Years
                      </button>
                      <button
                        className={`btn ${periodUnit === "months"
                          ? "btn-primary"
                          : "btn-outline-primary"
                          }`}
                        type="button"
                        onClick={() => setPeriodUnit("months")}
                      >
                        Months
                      </button>
                    </div>
                    {errorMessages.period && (
                      <div className="text-danger">{errorMessages.period}</div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label htmlFor="startDate" className="form-label">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="startDate"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        min={today}
                      />
                      {errorMessages.startDate && (
                        <div className="text-danger">
                          {errorMessages.startDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="endDate" className="form-label">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                      />
                      {errorMessages.endDate && (
                        <div className="text-danger">
                          {errorMessages.endDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="compoundInterval" className="form-label">
                        Compound Interval
                      </label>
                      <select
                        className="form-select"
                        id="compoundInterval"
                        value={compoundInterval}
                        onChange={(e) =>
                          setCompoundInterval(
                            e.target.value as "year" | "half-year" | "month"
                          )
                        }
                      >
                        <option value="year">Yearly</option>
                        <option value="half-year">Half-Yearly</option>
                        <option value="month">Monthly</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="d-flex justify-content-center " style={{ marginTop: '30px' }}>
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={handleCalculate}

                  >
                    Calculate
                  </button>
                </div>
                <div className='d-flex justify-content-center'>
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>
          {showResult && (
            <div className="col-12 col-md-6">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title">Result</h5>
                    <BootstrapTooltip title="Save Calculation" placement="top">
                      <IconButton color="secondary" onClick={handleDialogOpen}>
                        {icon}
                      </IconButton>
                    </BootstrapTooltip>
                  </div>
                  <p className="card-text d-flex justify-content-between">
                    <strong>Principal Amount:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {formatNumberInIndianStyle(principalForChart)}
                    </span>
                  </p>
                  <p className="card-text d-flex justify-content-between">
                    <strong>Total Interest:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {totalInterest !== null
                        ? formatNumberInIndianStyle(Math.round(totalInterest))
                        : ""}
                    </span>
                  </p>
                  <p className="card-text d-flex justify-content-between">
                    <strong>Maturity Amount:</strong>
                    <span className="text-end">
                      {currencySymbol}{" "}
                      {totalAmount !== null
                        ? formatNumberInIndianStyle(Math.round(totalAmount))
                        : ""}
                    </span>
                  </p>
                </div>
              </div>

              {/* Pie Chart Card */}
              {totalInterest !== null && totalAmount !== null && (
                <div className="card shadow-sm mt-4">
                  <div className="card-body text-center">
                    <h5 className="card-title mb-4">Interest Distribution</h5>
                    <div
                      style={{
                        width: "300px",
                        height: "285px",
                        margin: "0 auto",
                      }}
                    >
                      <Pie data={pieChartData} />
                    </div>
                  </div>
                </div>
              )}

            </div>

          )}

        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
      <div className="d-flex justify-content-end" style={{ marginTop: "64px", marginRight: '20px' }}>
        <InterestPdf
          principal={parseFloat(principal)}
          totalInterest={totalInterest !== null ? totalInterest : 0}
          totalAmount={totalAmount !== null ? totalAmount : 0}
          period={period}
          periodUnit={periodUnit}
          compoundInterval={compoundInterval}
          interestType={interestType}
          rate={isNaN(parseFloat(rate)) ? 0 : parseFloat(rate)}
          formatNumberWithCommas={formatNumberWithCommas}
          startDate={startDate}
          endDate={endDate}
        />
      </div>

    </>

  );
};

export default InterestCalculator;


